import React from 'react';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import { useModal } from '../../../../../components/Modal/PresentedModal';
import { t } from '../../../../../locales/translate';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { handleError } from '../../../../../model/notification';
import { FormForm } from '../../../../../components/Form/FormForm';
import { FormDatepicker } from '../../../../../components/Form/Datepicker/FormDatepicker';
import { graphql } from '../../../../../generated';
import { beginningOfDay, parseDate, tomorrow } from '../../../../../util/datetime';

const EVENT_AUTOMATION = graphql(`
  query EventAutomation($id: ID!) {
    event(id: $id) {
      id
      automations {
        nodes {
          id
          operation
          executeAt
        }
      }
    }
  }
`);

const CREATE_AUTOMATION = graphql(`
  mutation CreateEventAutomation(
    $automatableId: ID!
    $automatableType: AutomationAutomatableType!
    $executeAt: ISO8601DateTime!
    $operation: AutomationOperation!
  ) {
    createAutomation(
      input: {
        automatableId: $automatableId
        automatableType: $automatableType
        executeAt: $executeAt
        operation: $operation
      }
    ) {
      automation {
        id
        operation
        executeAt
      }
    }
  }
`);

const UPDATE_AUTOMATION = graphql(`
  mutation UpdateEventAutomation($id: ID!, $executeAt: ISO8601DateTime!) {
    updateAutomation(input: { id: $id, executeAt: $executeAt }) {
      automation {
        id
        operation
        executeAt
      }
    }
  }
`);

interface Props {
  id: string;
}

export function AutomationForm({ id }: Props): JSX.Element {
  const { data } = useSuspenseQuery(EVENT_AUTOMATION, { variables: { id } });
  const automation = data?.event?.automations?.nodes[0];

  const [create] = useMutation(CREATE_AUTOMATION, { refetchQueries: [EVENT_AUTOMATION] });
  const [update] = useMutation(UPDATE_AUTOMATION, { refetchQueries: [EVENT_AUTOMATION] });

  const { open, Modal, close } = useModal();

  const { fields, dirty, valid, onSubmit } = useForm<{ executeAt: Date }>({
    model: {
      executeAt: (automation && parseDate(automation.executeAt)) || beginningOfDay(tomorrow()),
    },
    onSubmit: async ({ model }) => {
      if (automation) {
        await update({
          variables: {
            id: automation.id,
            executeAt: model.executeAt.toISOString(),
          },
        });
      } else {
        await create({
          variables: {
            automatableId: id,
            automatableType: 'EVENT',
            executeAt: model.executeAt.toISOString(),
            operation: 'SEND_DELAYED_TICKETS',
          },
        });
      }
      close();
    },
    validations: {
      executeAt: 'required',
    },
    onSubmitError: handleError,
  });

  const handleDateChange = (value?: Date): void => {
    if (value) {
      fields.executeAt.onChange(value);
    }
  };

  return (
    <Modal
      open={open}
      title={automation ? t.model.automation.editAutomation : t.model.automation.setAutomation}
      size="xs"
    >
      <FormForm onSubmit={onSubmit} onClose={close} canSubmit={dirty && valid}>
        <FormDatepicker
          value={fields.executeAt.value}
          onChange={handleDateChange}
          errors={fields.executeAt.errors}
          name="executeAt"
          label={t.page.event.details.ticketDelay.sendTicketsAt}
          hourpicker
          disabledDays={[{ before: new Date() }]}
          hideClearButton
        />

        {automation && t.page.event.details.ticketDelay.removeAutomationHint}
      </FormForm>
    </Modal>
  );
}
